import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, Lock, Sparkles } from 'lucide-react';
import { useMembership } from '@/contexts/MembershipContext';
import {
  MEMBERSHIP_PLANS,
  KINSSPACE_PACKS,
  formattedSeatValue,
} from '@/lib/business/KinsCircleBusinessEngine';

const Pricing: React.FC = () => {
  const { openMembershipModal } = useMembership();
  const plans = Object.values(MEMBERSHIP_PLANS);
  const packs = Object.values(KINSSPACE_PACKS);

  return (
    <section id="pricing" className="relative py-20 bg-gradient-to-b from-white via-pink-50/40 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold uppercase tracking-wider mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            KinsCircle Plans
          </span>

          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
            Choose the plan that fits your family
          </h2>

          <p className="text-slate-600 max-w-2xl mx-auto">
            Every plan includes family seats, modern family tree tools, messaging, and KinsSpace for preserving memories.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="relative bg-white rounded-3xl border-2 border-fuchsia-100 p-7 shadow-lg flex flex-col"
            >
              <div className="mb-5">
                <div className="font-serif text-2xl font-bold text-slate-900">
                  {plan.name}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  {plan.tagline}
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-end gap-1">
                  <span className="font-serif text-5xl font-bold text-slate-900">
                    ${plan.monthlyPrice.toFixed(2)}
                  </span>
                  <span className="text-base text-slate-500 pb-1">/month</span>
                </div>

                <div className="mt-1 text-sm font-semibold text-fuchsia-700">
                  or ${plan.yearlyPrice.toFixed(2)}/year
                </div>
              </div>

              <div className="text-xs text-slate-500 mb-5">
                {formattedSeatValue(plan)}
              </div>

              <div className="rounded-xl bg-fuchsia-50 border border-fuchsia-100 p-3 mb-5 text-sm text-slate-700 space-y-1">
                <div><strong>{plan.seats}</strong> family seats</div>
                <div><strong>{plan.includedKinsSpaceGB} GB</strong> KinsSpace included</div>
                <div><strong>{plan.uploadLimitMB} MB</strong> max upload size</div>
              </div>

              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-fuchsia-500 mt-0.5 flex-shrink-0" />
                  Family tree building
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-fuchsia-500 mt-0.5 flex-shrink-0" />
                  Modern relationships, co-parents, step-family, and chosen family
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-fuchsia-500 mt-0.5 flex-shrink-0" />
                  Messaging and family connection tools
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 text-fuchsia-500 mt-0.5 flex-shrink-0" />
                  KinsSpace memory preservation
                </li>
              </ul>

              <Button
                onClick={() => openMembershipModal(plan.id)}
                size="lg"
                className="w-full rounded-full bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800 text-white shadow-lg shadow-fuchsia-500/20"
              >
                Start {plan.name}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">
            Need more KinsSpace?
          </h3>
          <p className="text-sm text-slate-600 mb-5">
            Add more space anytime without changing your family plan.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {packs.map((pack) => (
              <div key={pack.id} className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
                <div className="font-semibold text-slate-900">{pack.name}</div>
                <div className="text-sm text-slate-600 mt-1">
                  ${pack.monthlyPrice.toFixed(2)}/month
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  ${pack.yearlyPrice.toFixed(2)}/year
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-slate-500 flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" />
          Secure checkout powered by Stripe. Family memories are preserved; missed payments pause uploads instead of deleting memories.
        </div>
      </div>
    </section>
  );
};

export default Pricing;
